import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import css from "./Form.scss";
import { isEmpty } from "lodash";
import Select from "common/StyledSelect";
import { SingleDatePicker } from "react-dates";
import { getTimeOptions, repeatOptions } from "utils/reserveTime";
import useStarfishAPI from "hooks/useStarfishAPI";
import Icon from "SF/MaterialIcon";
import { 
  getTitleErrorMessage, 
  getDateErrorMessage, 
  getCategoryErrorMessage, 
  getCalendarErrorMessage, 
  getTimeErrorMessage
} from "../../utils/reserveTime";
import ErrorSpan from "./Error";

const Form = props => {
  const [startDateFocused, setStartDateFocused] = useState(false);
  const [endDateFocused, setEndDateFocused] = useState(false);
  const [response, callAPI] = useStarfishAPI({ isNewAPI: true });
  const hasFinishedLoadingCategories = useRef(false);

  const timeOptions = getTimeOptions();
  const displayCalendars = props.calendarOptions.length !== 0 && !props.isEdit;
  const endDateDisabled = props.repeat.value === false;

  const getCategoryPlaceholder = () => {
    if (response.pending || !hasFinishedLoadingCategories.current)
      return "Loading categories...";
    return props.categories.length === 0
      ? "Your institution does not have categories set up"
      : "Select a Category";
  };

  const getInputClass = (...errors) => {
    const classes = [css['input']];
    if ( errors.filter(error => error !== "").length > 0 ) classes.push(css['error']);
    return classes.join(" ");
  }

  const getColumnClass = (error, focused = false) => {
    const classes = [css['column']];
    if ( focused ) classes.push(css['focused']);
    if ( error ) classes.push(css['error']);
    return classes.join(" ");
  }

  const getError = field => {
    if ( !props.shouldValidate ) return "";
    switch (field) {
      case 'title':
        return getTitleErrorMessage(props.description);
      case 'startDate':
        return getDateErrorMessage(props.startDate);
      case 'endDate':
        return props.repeat.value === true ? getDateErrorMessage(props.endDate) : "";
      case 'category':
        return getCategoryErrorMessage(props.categories, props.category);
      case 'calendar':
        return getCalendarErrorMessage(props.calendarOptions, props.calendar);
      case 'startTime':
        return getTimeErrorMessage(props.startTime);
      case 'endTime':
        return getTimeErrorMessage(props.endTime);
    }
  };

  // Get all error messages
  const titleError = getError('title');
  const startDateError = getError('startDate');
  const endDateError = getError('endDate');
  const categoryError = getError('category');
  const calendarError = getError('calendar');
  const startTimeError = getError('startTime');
  const endTimeError = getError('endTime');

  const fetchCategories = useCallback(() => {
    callAPI(`../api/reserved-time-categories`);
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const updateState = useCallback((property, value) => {
    props.setValue(property, value);
  }, []);

  useEffect(() => {
    if ( !response.success ) return;
    
    // Fill categories dropdown
    hasFinishedLoadingCategories.current = true;
    const categories = response.data
    .filter(({ enabled }) => enabled)
    .map(({ title, id }) => ({
      label: title,
      value: id
    }));
    updateState('categories', categories);

    // If there is pre-selected category id, select the corresponding option
    if ( !props.categoryId ) return;
    const initialCategory = categories.find(({value}) => value === props.categoryId);
    if ( initialCategory ) updateState('category', initialCategory)
  }, [response, updateState, props.categoryId]);

  const onDateChange = (key, value, id) => {
    props.setValue(key, value);
    
    // If value is null due to an invalid/incomplete date, do not set focus
    // on the calendar button
    if ( value === null ) return;
    document
      .querySelector("#" + id)
      .parentElement
      .parentElement
      .querySelector("button")
      .focus();
  };

  return (
    <form className={css["form"]}>
      {displayCalendars && (
        <div className={getInputClass(calendarError)}>
          <label htmlFor="calendar-select">
            <span aria-hidden>*</span> Calendar
          </label>
          <div className={css['input-container']}>
            <Select
              id="calendar-select"
              value={props.calendar}
              onChange={option => props.setValue('calendar', option)}
              options={props.calendarOptions}
              isLoading={false}
              placeholder="Select Calendar Name"
              maxMenuHeight={300}
              isDisabled={props.calendarOptions.length < 2}
              hasError={!!calendarError}
            />
            <ErrorSpan error={calendarError} />
          </div>
        </div>
      )}
      <div className={getInputClass(categoryError)}>
        <label htmlFor="category-select">
          <span aria-hidden>*</span> Category
        </label>
        <div className={css['input-container']}>
          <Select
            id="category-select"
            value={props.category}
            onChange={option => props.setValue('category', option)}
            options={props.categories}
            isLoading={response.pending}
            placeholder={getCategoryPlaceholder()}
            maxMenuHeight={400}
            isDisabled={props.categories.length === 0}
            hasError={!!categoryError}
          />
          <ErrorSpan error={categoryError} />
        </div>
      </div>
      <div className={getInputClass(titleError)}>
        <label htmlFor="meeting-title">
          <span aria-hidden>*</span> Title
        </label>
        <div className={css['input-container']}>
          <input
            id="meeting-title"
            type="text"
            data-role="meetingTitle"
            value={props.description}
            onChange={e => props.setValue('description', e.target.value)}
          />
          <ErrorSpan error={titleError} />
        </div>
      </div>
      <div className={getInputClass(startDateError, startTimeError, endTimeError)}>
        <label>
          <span aria-hidden>*</span> When
        </label>
        <div className={css["row-3"]}>
          <div className={getColumnClass(startDateError, startDateFocused)}>
            <label htmlFor="date-select">Date</label>
            <SingleDatePicker
              date={props.startDate}
              onDateChange={date => onDateChange('startDate', date, "date-select")}
              focused={startDateFocused}
              onFocusChange={({ focused }) => setStartDateFocused(focused)}
              id="date-select"
              numberOfMonths={1}
              displayFormat="MM-DD-YYYY"
              placeholder="MM-DD-YYYY"
              showDefaultInputIcon
              inputIconPosition="after"
              small
              block
              noBorder
              hideKeyboardShortcutsPanel
              openDirection="up"
              isOutsideRange={date => date.isAfter(props.endDate)}
            />
            <ErrorSpan error={startDateError} />
          </div>
          <div className={getColumnClass(startTimeError)}>
            <label htmlFor="start-time-select">Start Time</label>
            <div className={css['input-container']}>
              <Select
                id="start-time-select"
                value={props.startTime}
                onChange={option => props.setValue('startTime', option)}
                options={timeOptions.filter(({ order }) => {
                  return isEmpty(props.endTime)
                    ? true
                    : props.endTime.order > order;
                })}
                isSearchable
                isClearable
                placeholder="Ex: 11:00 am"
                hideDropdownIcon
                hasError={!!startTimeError}
              />
              <ErrorSpan error={startTimeError} />
            </div>
          </div>
          <div className={getColumnClass(endTimeError)}>
            <label htmlFor="end-time-select">End Time</label>
            <div className={css['input-container']}>
              <Select
                id="end-time-select"
                value={props.endTime}
                onChange={option => props.setValue('endTime', option)}
                options={timeOptions.filter(({ order }) => {
                  return isEmpty(props.startTime)
                    ? true
                    : props.startTime.order < order;
                })}
                isSearchable
                isClearable
                placeholder="Ex: 11:30 am"
                hideDropdownIcon
                hasError={!!endTimeError}
              />
              <ErrorSpan error={endTimeError} />
            </div>
          </div>
        </div>
      </div>
      {!props.isEdit && <div className={css["input"]}>
        <label htmlFor="repeats-select" className={css["repeats-label"]}>
          Repeats
        </label>
        <div className={css["row-2"]}>
          <div className={css["column"]}>
            <Select
              id="repeats-select"
              value={props.repeat}
              onChange={option => props.setValue('repeat', option)}
              options={repeatOptions}
            />
          </div>
          <div className={getColumnClass(endDateError, endDateFocused)}>
            <label htmlFor="end-date-select">
              {props.repeat.value && <span aria-hidden>*</span>} Until
            </label>
            <SingleDatePicker
              date={props.endDate}
              onDateChange={date => onDateChange('endDate', date, "end-date-select")}
              focused={endDateFocused}
              onFocusChange={({ focused }) => setEndDateFocused(focused)}
              id="end-date-select"
              numberOfMonths={1}
              displayFormat="MM-DD-YYYY"
              placeholder="MM-DD-YYYY"
              showDefaultInputIcon
              customInputIcon={
                endDateDisabled ? (
                  <Icon className={css["lock-icon"]} icon="lock" />
                ) : null
              }
              inputIconPosition="after"
              small
              block
              noBorder
              hideKeyboardShortcutsPanel
              disabled={endDateDisabled}
              isOutsideRange={date => date.isBefore(props.startDate)}
              openDirection="up"
            />
            <ErrorSpan error={endDateError} />
          </div>
        </div>
      </div>}
    </form>
  );
};

Form.propTypes = {
  setValue: PropTypes.func.isRequired,
  calendarOptions: PropTypes.array.isRequired,
  calendar: PropTypes.object,
  categories: PropTypes.array.isRequired,
  category: PropTypes.object,
  categoryId: PropTypes.number,
  description: PropTypes.string,
  startDate: PropTypes.object,
  endDate: PropTypes.object,
  startTime: PropTypes.object,
  endTime: PropTypes.object,
  repeat: PropTypes.object.isRequired,
  shouldValidate: PropTypes.bool.isRequired,
  isEdit: PropTypes.bool.isRequired
};

export default Form;
